/** Навигация витрины: три вкладки, у каждой своя история.
 *
 * Внутри Telegram адресной строки нет, зато есть системная кнопка «назад».
 * Поэтому история — стек: положили экран, сняли экран.
 *
 * Стеков три, по одному на вкладку. Это не украшение: человек уходит из
 * каталога в игры и возвращается — и должен оказаться там, где был, а не в
 * корне. Общий стек такое поведение выразить не может, а именно его ждут от
 * нижней навигации: вкладка помнит своё.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { backButton, tap } from '@/shared/tg';

export type Screen =
  | { type: 'menu'; id: string; title: string }
  | { type: 'list'; id: string; title: string }
  | { type: 'product'; id: string; listId: string; title: string }
  | { type: 'message'; id: string; title: string; text: string }
  | { type: 'games'; title: string }
  | { type: 'game'; id: string; title: string }
  | { type: 'account'; title: string }
  | { type: 'my-orders'; title: string }
  | { type: 'market'; title: string }
  | { type: 'referral'; title: string }
  | { type: 'cart'; title: string }
  | { type: 'raffle'; title: string };

export type Tab = 'catalog' | 'games' | 'cart' | 'raffle' | 'account';

/** Корень вкладки: то, что видно, когда история пуста. */
function root(tab: Tab, catalogRoot: string): Screen {
  if (tab === 'games') return { type: 'games', title: 'Игры и бонусы' };
  if (tab === 'cart') return { type: 'cart', title: 'Корзина' };
  if (tab === 'raffle') return { type: 'raffle', title: 'Розыгрыш' };
  if (tab === 'account') return { type: 'account', title: 'Аккаунт' };
  return { type: 'menu', id: catalogRoot, title: 'Каталог' };
}

export function useNavigation(catalogRoot: string) {
  const [tab, setTab] = useState<Tab>('catalog');
  const [stacks, setStacks] = useState<Record<Tab, Screen[]>>(() => ({
    catalog: [root('catalog', catalogRoot)],
    games: [root('games', catalogRoot)],
    cart: [root('cart', catalogRoot)],
    raffle: [root('raffle', catalogRoot)],
    account: [root('account', catalogRoot)],
  }));

  const stack = stacks[tab];

  const push = useCallback((screen: Screen) => {
    setStacks((previous) => ({ ...previous, [tab]: [...previous[tab], screen] }));
    window.scrollTo(0, 0);
  }, [tab]);

  const pop = useCallback(() => {
    setStacks((previous) => {
      const current = previous[tab];
      return current.length > 1
        ? { ...previous, [tab]: current.slice(0, -1) }
        : previous;
    });
    window.scrollTo(0, 0);
  }, [tab]);

  /** Прыжок по крошкам: обрезаем стек до нужной глубины. */
  const goTo = useCallback((depth: number) => {
    setStacks((previous) => ({ ...previous, [tab]: previous[tab].slice(0, depth + 1) }));
    window.scrollTo(0, 0);
  }, [tab]);

  /** Открыть сразу нужную глубину: кнопка из чата ведёт в конкретный раздел. */
  const openPath = useCallback((path: Screen[]) => {
    if (!path.length) return;
    setTab('catalog');
    setStacks((previous) => ({ ...previous, catalog: path }));
  }, []);

  /** Переключить вкладку. Повторный тап по своей — возврат в её корень. */
  const openTab = useCallback((next: Tab) => {
    tap();
    setTab((was) => {
      if (was === next) {
        setStacks((previous) => ({ ...previous, [next]: previous[next].slice(0, 1) }));
      }
      return next;
    });
    window.scrollTo(0, 0);
  }, []);

  // Системная кнопка «назад» в Telegram должна вести туда же, куда наша
  useEffect(() => {
    backButton(stack.length > 1, () => {
      tap();
      pop();
    });
  }, [stack.length, pop]);

  const current = stack[stack.length - 1] as Screen;
  // Верхний экран каждой вкладки: соседей нужно уметь нарисовать, не переходя
  // в них, — иначе листать пальцем нечего
  const tops = useMemo(
    () => ({
      catalog: stacks.catalog[stacks.catalog.length - 1] as Screen,
      games: stacks.games[stacks.games.length - 1] as Screen,
      cart: stacks.cart[stacks.cart.length - 1] as Screen,
      raffle: stacks.raffle[stacks.raffle.length - 1] as Screen,
      account: stacks.account[stacks.account.length - 1] as Screen,
    }),
    [stacks],
  );

  return useMemo(
    () => ({
      tab, stack, stacks, tops, current, push, pop, goTo, openPath, openTab,
      atRoot: stack.length === 1,
    }),
    [tab, stack, stacks, tops, current, push, pop, goTo, openPath, openTab],
  );
}
